import cn from 'classnames';
import React, {FC, useMemo} from 'react';

import AemButton from '@/components/Aem/AemButton';
import {AppForm} from '@/components/AppForm';
import RemoteIconV2 from '@/components/Icon/RemoteIconV2';
import appConfig from '@/config/appConfig.typed';
import {OTP_TYPE_CODE, OtpTypeCode} from '@/consts/loginTypes';
import appContent from '@/services/content/appContent.typed';
import log from '@/services/logger/log';
import {twx} from '@/utils/tailwindUtils';

import useAuthForm from '../../hooks/useAuthForm';
import type {OtpIdentifierSubmitParams} from '../../hooks/useOtpLoginHandlers';
import type {LoginErrorDetails} from '../../LoginPageV2.types';

interface OtpIdentifierFormData {
    email?: string;
    phone?: string;
}

interface OtpIdentifierFormProps {
    channel: OtpTypeCode;
    onSubmit?: (data: OtpIdentifierSubmitParams) => void;
    error?: LoginErrorDetails;
    setError?: (isError: LoginErrorDetails) => void;
    className?: string;
}

const EMAIL_FIELD_NAME = 'email';
const PHONE_FIELD_NAME = 'phone';

const CountryCodeField: FC<{phoneNumberCode?: string}> = ({phoneNumberCode}) => {
    if (!phoneNumberCode) {
        return null;
    }

    return (
        <div className='flex h-full items-center justify-center border-r border-primary-dark-25 px-2 pt-[1px] text-14 tracking-04'>
            {phoneNumberCode}
        </div>
    );
};

const OtpIdentifierForm: FC<OtpIdentifierFormProps> = ({channel, onSubmit, error, setError, className}) => {
    const isEmail = channel === OTP_TYPE_CODE.EMAIL;
    const [country] = appConfig.getCountrySettingsList();

    const phoneCountryCode = country?.phoneNumberCodes?.[0];

    const {
        loginButton,
        phoneHint,
        emailHint,
        emailPlaceholder,
        phonePlaceholder,
        emailRegexWarning,
        phoneRegexWarning,
    } = appContent.getOtpContent();

    const {phoneRegex, emailRegex} = appConfig.getOtpSettings();

    const fieldName = isEmail ? EMAIL_FIELD_NAME : PHONE_FIELD_NAME;
    const regexString = isEmail ? emailRegex : phoneRegex;

    const patternRegex = useMemo(() => {
        if (!regexString) return undefined;
        try {
            return new RegExp(regexString);
        } catch (e) {
            log.info(`Invalid Regex from AEM: ${regexString}`);
            return undefined;
        }
    }, [regexString]);

    const {control, handleFormSubmit, handleFormKeyDown, fieldStates, clearField} = useAuthForm<OtpIdentifierFormData>({
        fields: [{name: fieldName, pattern: patternRegex}],
        defaultValues: {[fieldName]: ''} as OtpIdentifierFormData,
        setError,
        onSubmit: (data) => {
            const identifier = data[fieldName as keyof OtpIdentifierFormData] || '';

            onSubmit?.({identifier, phoneCountryCode});
        },
    });

    const currentField = fieldStates[fieldName];
    const hasValidationIssues = !currentField.isEntered || !currentField.isValid;
    const warning = hasValidationIssues ? (isEmail ? emailRegexWarning : phoneRegexWarning) : undefined;

    const handleInputChange = (field: any) => (event: React.ChangeEvent<HTMLInputElement>) => {
        if (setError) setError(null);

        if (!isEmail) {
            event.target.value = event.target.value.replace(/\D/g, '');
        }

        field.onChange(event);
    };

    const handleInputFocus = (fieldName: string) => () => {
        if (error) {
            clearField(fieldName as any);
        }
    };

    const inputSpecificProps = isEmail
        ? {
              type: 'email',
              placeholder: emailPlaceholder,
              className: twx(currentField.className, !hasValidationIssues && 'px-3'),
          }
        : {
              type: 'tel',
              placeholder: phonePlaceholder,
              maxLength: 15,
              containerClassName: cn(
                  currentField.className,
                  'rounded-sm flex items-center p-0 focus-within:border-primary'
              ),
              className: twx('flex h-full w-full border-0 px-3', {
                  'pr-10 rtl:pl-10': hasValidationIssues,
              }),
              children: <CountryCodeField phoneNumberCode={phoneCountryCode} />,
          };

    return (
        <AppForm
            className={twx('mt-8 w-full text-left ml:mt-6', className)}
            onSubmit={handleFormSubmit}
            onKeyDown={handleFormKeyDown}
            noValidate
        >
            <AppForm.Body>
                <AppForm.Field
                    name={fieldName}
                    control={control}
                    rules={{required: true, pattern: patternRegex}}
                    warningMessage={warning?.title}
                >
                    {({field}) => (
                        <>
                            <AppForm.FieldLabel className='mb-4 font-medium'>
                                {isEmail ? emailHint : phoneHint}
                            </AppForm.FieldLabel>

                            <AppForm.FieldInput
                                {...inputSpecificProps}
                                onChange={handleInputChange(field)}
                                onFocus={handleInputFocus(fieldName)}
                                onBlur={field.onBlur}
                                warningIcon={warning && <RemoteIconV2 className='h-6 w-6' icon={warning} />}
                                value={field.value}
                            />

                            <AppForm.FieldWarningMessage />
                        </>
                    )}
                </AppForm.Field>
            </AppForm.Body>

            <AemButton
                primary
                light
                aria-label={loginButton?.alt}
                type='submit'
                className={twx('m-auto mt-6 bg-primary-dark ml:w-full', {'mb-5': error})}
                {...loginButton}
            />
        </AppForm>
    );
};

export default OtpIdentifierForm;
